import { useRouter } from "@/lib/nav";
import { useState } from "react";

import { Button } from "@buildpy/ui/button";
import ConfirmDialog from "@buildpy/ui/confirm-dialog";
import { Textarea } from "@buildpy/ui/textarea";
import { settleRedirect } from "@/lib/redirect-error";
import { deleteStaffMessage, editStaffMessage } from "@/api/actions/message";
import { deleteCustomerMessageByLink, editCustomerMessageByLink } from "@/api/actions/message-share";
import type { MessageActionResult } from "@buildpy/core/modules/message/action-types";
import { formatDateTime } from "@buildpy/core/lib/format";
import { MESSAGE_DELETED_LABEL } from "@buildpy/core/modules/message/rules";
import { MAX_MESSAGE_BODY, type MessageItem } from "@buildpy/core/modules/message/types";

// 고치고 지우는 쪽 — 직원은 현장, 고객은 링크 토큰으로 부른다.
export type MessageChangeTarget = { kind: "staff"; projectId: string } | { kind: "customer"; token: string };

interface Props {
  message: MessageItem;
  target: MessageChangeTarget;
  testId: string;
}

const FAILED = "처리하지 못했습니다. 잠시 뒤 다시 해 주세요";

// seen = 화면이 본 버전(edited_at) — 다른 탭에서 바뀌었으면 서버가 stale 로 돌려보낸다.
const edit = (target: MessageChangeTarget, message: MessageItem, body: string): Promise<MessageActionResult> => {
  const seen = { message_id: message.id, seen_edited_at: message.edited_at, body };
  return target.kind === "staff"
    ? editStaffMessage({ project_id: target.projectId, ...seen })
    : editCustomerMessageByLink({ token: target.token, ...seen });
};

const remove = (target: MessageChangeTarget, message: MessageItem): Promise<MessageActionResult> => {
  const seen = { message_id: message.id, seen_edited_at: message.edited_at };
  return target.kind === "staff"
    ? deleteStaffMessage({ project_id: target.projectId, ...seen })
    : deleteCustomerMessageByLink({ token: target.token, ...seen });
};

// 내 글 한 줄의 「고치기·지우기」 — 고치기는 그 자리 입력칸, 지우기는 확인 뒤 흔적만 남긴다.
const MessageChanger = ({ message, target, testId }: Props) => {
  const router = useRouter();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body);
  const [asking, setAsking] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const tooLong = Array.from(draft).length > MAX_MESSAGE_BODY;
  const blank = draft.trim() === "" && !message.has_photo;
  // 고객은 휴대폰 화면이라 누르는 칸을 키운다 · 단추 이름에 어느 글인지(쓴 시각)를 넣는다.
  const size = target.kind === "customer" ? "xs-touch" : "xs";
  const when = formatDateTime(message.created_at);

  const run = async (call: () => Promise<MessageActionResult>, done: () => void) => {
    setPending(true);
    setError(null);
    setStale(false);
    try {
      const result = await call();
      if (!result.ok) {
        setError(result.message);
        setStale(result.stale === true);
        if (result.reverify || result.closed) router.refresh();
        return;
      }
      done();
      router.refresh();
    } catch (e) {
      if (settleRedirect(e)) return;
      setError(FAILED);
    } finally {
      setPending(false);
    }
  };

  // 새로 불러오면 바뀐 글로 다시 그려진다(목록의 key 가 edited_at 을 담는다) — 입력칸은 닫는다.
  const reload = () => {
    setStale(false);
    setError(null);
    setEditing(false);
    setDraft(message.body);
    router.refresh();
  };

  return (
    <div className="mt-1 flex flex-col gap-y-1" data-testid={testId}>
      {editing ? (
        <form
          className="flex flex-col gap-y-1"
          onSubmit={(event) => {
            event.preventDefault();
            void run(() => edit(target, message, draft), () => setEditing(false));
          }}>
          <Textarea
            aria-label="고칠 내용"
            className="min-h-[64px]"
            data-testid={`${testId}-body`}
            disabled={pending}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className="flex justify-end gap-x-2">
            <Button
              disabled={pending}
              size={size}
              type="button"
              variant="link-muted"
              onClick={() => {
                setEditing(false);
                setDraft(message.body);
                setError(null);
              }}>
              취소
            </Button>
            <Button data-testid={`${testId}-save`} disabled={pending || blank || tooLong} size={size} type="submit" variant="link">
              {pending ? "저장 중" : "저장"}
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex justify-end gap-x-2">
          <Button aria-label={`${when} 글 고치기`} data-testid={`${testId}-edit`} disabled={pending} size={size} type="button" variant="link-muted" onClick={() => setEditing(true)}>
            고치기
          </Button>
          <Button aria-label={`${when} 글 지우기`} data-testid={`${testId}-delete`} disabled={pending} size={size} type="button" variant="link-destructive" onClick={() => setAsking(true)}>
            지우기
          </Button>
        </div>
      )}
      {error && (
        <div className="flex items-center justify-end gap-x-2">
          <p className="text-xs text-destructive" data-testid={`${testId}-error`} role="alert">
            {error}
          </p>
          {stale && (
            <Button data-testid={`${testId}-reload`} size={size} type="button" variant="link" onClick={reload}>
              새로 불러오기
            </Button>
          )}
        </div>
      )}
      <ConfirmDialog
        destructive
        confirmLabel="지우기"
        description={`지운 글은 「${MESSAGE_DELETED_LABEL}」로 남고 되돌릴 수 없습니다.`}
        open={asking}
        testId={`${testId}-confirm`}
        title="이 글을 지울까요?"
        onConfirm={() => void run(() => remove(target, message), () => setEditing(false))}
        onOpenChange={setAsking}
      />
    </div>
  );
};

export default MessageChanger;
